import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

import api from "apiSingleton";
import SvgIcon from "components/base/SvgIcon";
import {
  APPLICATION_TAB_NAMES,
  JOB_APPLICATION_MATCH_STATUS,
  JOB_APPLICATION_MATCH_STATUS_FORMATTED,
  JOB_OPPORTUNITY_STATUSES,
} from "lookup";
import { capitalize } from "lodash";
import dayjs from "dayjs";
import { SquareArrowOutUpRight } from "lucide-react";
import UserInfoModal from "containers/ModalContainer/modals/UserInfoModal";
import { cn } from "utils";
import {
  PROFILE_SECTION_TITLE_CLASSNAME,
  PROFILE_MORE_LINK_CLASSNAME,
} from "../../constants";
import { ProfileMoreLinkLabel } from "../../ProfileMoreLinkLabel";
import {
  MATCHER_LABEL,
  formatMatcher,
} from "components/UserCardList/helpers/userCard";

const formatMatchJobStatus = (status) => {
  const formattedStatus = JOB_APPLICATION_MATCH_STATUS_FORMATTED[status];
  return formattedStatus ?? capitalize(status);
};

const compareDates = (a, b) => {
  const dateA = new Date(a.updatedAt || a.createdAt);
  const dateB = new Date(b.updatedAt || b.createdAt);
  return dateB - dateA;
};

const getJobName = (jobOpp) => {
  const title = jobOpp
    ? jobOpp.status === JOB_OPPORTUNITY_STATUSES.FULFILLED
      ? `${jobOpp.title} (${jobOpp.status?.toLowerCase()})`
      : jobOpp.title || "-"
    : "-";

  if (title === "-") {
    return <>{title}</>;
  }

  return (
    <Link
      to={`/jobOpps/${jobOpp.id}/details`}
      target="_blank"
      className="text-sky-500 cursor-pointer hover:underline"
      title={title}
    >
      {title}
    </Link>
  );
};

const isNotAutoShortlisted = (match) => {
  if (
    match.status === JOB_APPLICATION_MATCH_STATUS.SHORTLISTED &&
    match.reasonForAutoMatch?.startsWith("Auto shortlisted")
  ) {
    return false;
  }
  return true;
};

const formatMatches = (applications, matches) => {
  // Create a map for quick lookup of isNotActive status
  const applicationMap =
    applications?.reduce((acc, app) => {
      acc[app.id] = app.isNotActive || false;
      return acc;
    }, {}) || {};

  // Iterate over matches and add hasInactiveApplication property
  const formattedMatches =
    matches?.map((match) => {
      return {
        ...match,
        hasInactiveApplication: applicationMap[match.applicationId] === true,
      };
    }) || [];

  const nonSkippedNonAutoShortlistedMatches = formattedMatches.filter(
    (match) =>
      match.status !== JOB_APPLICATION_MATCH_STATUS.SKIPPED &&
      isNotAutoShortlisted(match)
  );

  return nonSkippedNonAutoShortlistedMatches.sort(compareDates);
};

const tableCellClassName = "min-w-0 truncate px-4 py-3";
const tableGridClassName =
  "grid min-w-[900px] grid-cols-[minmax(220px,2fr)_180px_minmax(140px,1fr)_150px_minmax(180px,1fr)_130px] items-center";

const MatchItem = ({ match, user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const jobName = getJobName(match.jobOpportunity);
  const status = formatMatchJobStatus(match.status);
  const date = match.updatedAt
    ? dayjs(match.updatedAt).format("MM/DD/YYYY")
    : "-";
  const organization = match.jobOpportunity?.organization || "-";
  const matcher = formatMatcher(match.matcher);

  return (
    <div
      className={`${tableGridClassName} rounded-lg bg-slate-50 text-sm text-slate-600`}
      key={match.id}
    >
      <span className={`${tableCellClassName} font-semibold text-sky-500`}>
        <span className="block truncate" title={match.jobOpportunity?.title}>
          {jobName}
        </span>
      </span>

      <span className={`${tableCellClassName} flex items-center gap-2`}>
        <span className="truncate" title={status}>
          {status}
        </span>
        {match.hasInactiveApplication && (
          <SvgIcon
            type="warning"
            className="h-4 w-4 shrink-0 cursor-help text-amber-600"
            title="This match is associated with a job role that is no longer active for this user"
          />
        )}
      </span>

      <span className={tableCellClassName} title={matcher}>
        {matcher}
      </span>

      <span className={tableCellClassName}>{date}</span>

      <span className={tableCellClassName} title={organization}>
        {organization}
      </span>

      <span className={`${tableCellClassName} flex justify-end`}>
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="inline-flex items-center gap-2 whitespace-nowrap font-semibold text-slate-700 transition hover:text-sky-600"
        >
          View details
          <SquareArrowOutUpRight className="h-4 w-4" />
        </button>
        <UserInfoModal
          open={isOpen}
          onOpenChange={setIsOpen}
          match={match}
          user={user}
          tabDefaultValue="match-history"
        />
      </span>
    </div>
  );
};

const MatchedJobsTable = ({ matches, user }) => {
  if (!matches?.length) {
    return <div>There are no matched jobs for this user</div>;
  }

  return (
    <div className="w-full overflow-x-auto">
      <div
        className={`${tableGridClassName} mb-2 rounded-lg bg-slate-100 text-sm font-semibold text-slate-700`}
      >
        <span className={tableCellClassName}>Job</span>
        <span className={tableCellClassName}>Status</span>
        <span className={tableCellClassName}>{MATCHER_LABEL}</span>
        <span className={tableCellClassName}>Date</span>
        <span className={tableCellClassName}>Organization</span>
        <span className={tableCellClassName} />
      </div>

      <div className="space-y-2">
        {matches.map((match) => (
          <MatchItem key={match.id} match={match} user={user} />
        ))}
      </div>
    </div>
  );
};

const MatchedJobs = ({ hit, activeTabName }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showAllMatchedJobs, setShowAllMatchedJobs] = useState(false);

  const userMatchedJobs = useRef([]);

  const handleToggleCollapse = async () => {
    if (activeTabName === APPLICATION_TAB_NAMES.SEARCHRESULTS) {
      return;
    } else if (!isLoading) {
      // The data has been loaded earlier. No need to fetch it again
      return;
    }

    let applications = await api.user.getAllApplicationsForUser({
      userId: hit.id,
    });

    let matches = await api.match.getMatchesForUser({
      userId: hit.id,
    });

    userMatchedJobs.current = formatMatches(applications, matches);

    setIsLoading(false);
  };

  useEffect(() => {
    if (activeTabName === APPLICATION_TAB_NAMES.SEARCHRESULTS) {
      userMatchedJobs.current = formatMatches(hit?.applications, hit?.matches);

      setIsLoading(false);
    }
  }, [activeTabName, hit?.applications, hit?.matches]);

  useEffect(() => {
    handleToggleCollapse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabName, hit.id]);

  // Retaining existing behaviour - we don't show the accordion for search results tab if there are no matched jobs
  // For others, we are not yet sure because the user has to expand the accordion to trigger the api request
  // that will fetch (if any) the matches
  if (
    (activeTabName === APPLICATION_TAB_NAMES.SEARCHRESULTS &&
      userMatchedJobs.current?.length === 0) ||
    activeTabName === APPLICATION_TAB_NAMES.SKIPPED
  ) {
    return null;
  }

  return (
    <section className="talent-card-profile-section">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className={PROFILE_SECTION_TITLE_CLASSNAME}>
          Matched/applied history
        </h3>
      </div>
      {isLoading && <div className="text-gray-400">Loading...</div>}
      {!isLoading && (
        <>
          <MatchedJobsTable
            matches={
              showAllMatchedJobs
                ? userMatchedJobs.current
                : userMatchedJobs.current?.slice(0, 3)
            }
            user={hit}
          />
          {userMatchedJobs.current?.length > 3 && (
            <button
              type="button"
              className={PROFILE_MORE_LINK_CLASSNAME}
              onClick={() => setShowAllMatchedJobs((prev) => !prev)}
            >
              {showAllMatchedJobs ? (
                "Less"
              ) : (
                <ProfileMoreLinkLabel
                  count={userMatchedJobs.current.length - 3}
                />
              )}
              <SvgIcon
                type="chevronDown"
                className={cn("h-4 w-4 text-slate-600 transition-transform", {
                  "rotate-180": showAllMatchedJobs,
                })}
              />
            </button>
          )}
        </>
      )}
    </section>
  );
};

MatchedJobs.propTypes = {
  hit: PropTypes.object,
  activeTabName: PropTypes.string,
};

MatchedJobs.defaultProps = {
  hit: {},
  activeTabName: APPLICATION_TAB_NAMES.SEARCHRESULTS,
};

export default MatchedJobs;
